import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, Clock, Check, X, Truck, ArrowLeft } from 'lucide-react';
import Header from '@/components/Header';
import StatusBadge from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { getReturnRequest } from '@/lib/api';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '@/lib/utils';

const statusSteps = [
  { key: 'pending', label: 'In Attesa', icon: Clock },
  { key: 'approved', label: 'Approvato', icon: Check },
  { key: 'shipped', label: 'Spedito', icon: Truck },
  { key: 'completed', label: 'Completato', icon: Package },
];

const TrackRequest = () => {
  const { requestId } = useParams();
  const [request, setRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadRequest = async () => {
      try {
        const data = await getReturnRequest(requestId);
        setRequest(data);
      } catch (error) {
        console.error('Error loading request:', error);
      }
      setIsLoading(false);
    };
    
    loadRequest();
  }, [requestId]);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'd MMMM yyyy, HH:mm', { locale: it });
    } catch {
      return dateString;
    }
  };

  const getCurrentStepIndex = () => {
    if (request?.status === 'rejected') return -1;
    return statusSteps.findIndex(s => s.key === request?.status);
  };

  if (isLoading) {
    return (
      <div className="page-container bg-white">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <p>Caricamento...</p>
        </main>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="page-container bg-white">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg mb-4">Richiesta non trovata</p>
            <Link to="/">
              <Button className="luxury-btn-secondary">Torna alla Home</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const currentStep = getCurrentStepIndex();

  return (
    <div className="page-container bg-white">
      <Header />
      
      <main className="flex-1 py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-8">
              <ArrowLeft className="w-4 h-4" />
              Torna alla Home
            </Link>
            
            <div className="flex items-center justify-between mb-8">
              <div>
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2">
                  Tracciamento Richiesta
                </p>
                <h1 className="font-heading text-3xl font-normal tracking-tight">
                  {requestId?.slice(0, 8).toUpperCase()}
                </h1>
              </div>
              <StatusBadge status={request.status} />
            </div>
          </motion.div>

          {/* Progress Steps */}
          {request.status !== 'rejected' && (
            <motion.div 
              className="mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="flex items-center justify-between relative">
                {/* Progress Line */}
                <div className="absolute top-6 left-0 right-0 h-0.5 bg-border" />
                <div 
                  className="absolute top-6 left-0 h-0.5 bg-primary transition-all duration-500"
                  style={{ width: `${(currentStep / (statusSteps.length - 1)) * 100}%` }}
                />
                
                {statusSteps.map((step, index) => {
                  const Icon = step.icon;
                  const isActive = index <= currentStep;
                  const isCurrent = index === currentStep;
                  
                  return (
                    <div key={step.key} className="relative z-10 flex flex-col items-center">
                      <div className={cn(
                        "w-12 h-12 flex items-center justify-center border-2 bg-white transition-all",
                        isActive ? "border-primary" : "border-border",
                        isCurrent && "ring-4 ring-primary/20"
                      )}>
                        <Icon className={cn(
                          "w-5 h-5",
                          isActive ? "text-primary" : "text-muted-foreground"
                        )} />
                      </div>
                      <span className={cn(
                        "mt-3 text-xs font-medium uppercase tracking-wider",
                        isActive ? "text-primary" : "text-muted-foreground"
                      )}>
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Rejected Status */}
          {request.status === 'rejected' && (
            <motion.div 
              className="luxury-card p-8 mb-8 border-red-200 bg-red-50"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-red-100 flex items-center justify-center">
                  <X className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-medium text-red-900">Richiesta Rifiutata</h3>
                  {request.admin_notes && (
                    <p className="text-sm text-red-700 mt-1">{request.admin_notes}</p>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Request Details */}
          <motion.div 
            className="luxury-card p-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-6">
              Dettagli Richiesta
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Tipo</p>
                <p className="font-medium capitalize">
                  {request.request_type === 'exchange' ? 'Cambio' : 'Reso'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Ordine</p>
                <p className="font-medium">{request.order_number}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Data Richiesta</p>
                <p className="font-medium">{formatDate(request.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Ultimo Aggiornamento</p>
                <p className="font-medium">{formatDate(request.updated_at)}</p>
              </div>
            </div>

            {request.tracking_number && (
              <div className="border-t border-border pt-6 mb-6">
                <p className="text-xs text-muted-foreground mb-1">Numero Tracking Spedizione</p>
                <p className="font-mono text-lg">{request.tracking_number}</p>
              </div>
            )}

            <div className="border-t border-border pt-6">
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-4">
                Prodotti da Rendere
              </p>
              <div className="space-y-3">
                {request.return_items?.map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-12 h-16 bg-secondary overflow-hidden flex-shrink-0">
                      {item.image_url && (
                        <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.variant_title}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {request.exchange_items && request.exchange_items.length > 0 && (
              <div className="border-t border-border pt-6 mt-6">
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-4">
                  Prodotti Sostitutivi
                </p>
                <div className="space-y-3">
                  {request.exchange_items.map((item, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className="w-12 h-16 bg-secondary overflow-hidden flex-shrink-0">
                        {item.image_url && (
                          <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{item.title}</p>
                        <p className="text-sm text-muted-foreground">{item.variant_title}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {request.admin_notes && request.status !== 'rejected' && (
              <div className="border-t border-border pt-6 mt-6">
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2">
                  Note Admin
                </p>
                <p className="text-sm">{request.admin_notes}</p>
              </div>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default TrackRequest;
